import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from 'react-query'
import { FileText, Calendar, Send, XCircle, TriangleAlert, MoreHorizontal, Eye, Pencil, Ban, Trash2 } from 'lucide-react'

import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { emailCampaignService } from '@/services/emailCampaignService'
import { cn } from '@/lib/utils'
import { formatDateTime } from '@/lib/dateUtils'

const statusVariant = {
  DRAFT: 'secondary',
  SCHEDULED: 'default',
  SENDING: 'default',
  SENT: 'secondary',
  CANCELLED: 'outline',
  FAILED: 'outline',
}

const STATUS_ICON = {
  DRAFT: FileText,
  SCHEDULED: Calendar,
  SENDING: Send,
  SENT: Send,
  CANCELLED: XCircle,
  FAILED: TriangleAlert,
}

const formatRate = (rate) => `${rate}%`

const headerTimestamp = (campaign) => {
  let when
  if (campaign.status === 'SCHEDULED') when = campaign.scheduled_at
  else if (campaign.status === 'SENT' || campaign.status === 'SENDING') when = campaign.sent_at || campaign.scheduled_at
  else when = campaign.updated_at || campaign.created_at
  return when ? formatDateTime(when) : '—'
}

const StatMetric = ({ label, value, rate }) => (
  <div className="min-w-0">
    <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
    <p className="mt-1 flex items-baseline gap-1.5 text-sm font-medium">
      {value.toLocaleString()}
      <span className="text-xs font-normal text-muted-foreground">{formatRate(rate)}</span>
    </p>
  </div>
)

const CampaignCard = ({ campaign, onEdit, onCancel, onDelete }) => {
  const navigate = useNavigate()
  const Icon = STATUS_ICON[campaign.status] || Send
  const editable = ['DRAFT', 'SCHEDULED'].includes(campaign.status)
  const cancellable = ['DRAFT', 'SCHEDULED', 'SENDING'].includes(campaign.status)
  const wantsAnalytics = campaign.status === 'SENT' || campaign.status === 'SENDING'

  const { data } = useQuery(
    ['campaign-summary-card', campaign.id],
    () => emailCampaignService.getSummary(campaign.id),
    { enabled: wantsAnalytics }
  )
  const summary = data?.data

  const open = () => navigate(`/marketing-email/campaigns/${campaign.id}`)

  return (
    <Card
      role="button"
      tabIndex={0}
      onClick={open}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          open()
        }
      }}
      className="cursor-pointer overflow-hidden transition-all hover:-translate-y-px hover:shadow-md"
    >
      <div className={`flex items-start justify-between gap-3 px-5 pt-4 pb-4 ${summary ? 'border-b bg-background' : ''}`}>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <Badge
              variant={statusVariant[campaign.status] || 'secondary'}
              className={cn(
                campaign.status === 'SENT' && 'border-transparent bg-emerald-50 text-emerald-700 hover:bg-emerald-50 dark:bg-emerald-950/30 dark:text-emerald-400'
              )}
            >
              {campaign.status}
            </Badge>
            <span className="truncate text-xs text-muted-foreground">· {headerTimestamp(campaign)}</span>
          </div>
          <h3 className="mt-2.5 truncate text-sm font-semibold">{campaign.name}</h3>
          <p className="mt-0.5 truncate text-xs text-muted-foreground">
            Subject: {campaign.template?.subject_line?.trim() || 'No subject'}
          </p>
        </div>
        <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuItem onClick={open}>
                <Eye className="mr-2 h-4 w-4" />
                View Details
              </DropdownMenuItem>
              {editable && (
                <DropdownMenuItem onClick={() => onEdit(campaign)}>
                  <Pencil className="mr-2 h-4 w-4" />
                  Edit
                </DropdownMenuItem>
              )}
              {cancellable && (
                <DropdownMenuItem onClick={() => onCancel(campaign)}>
                  <Ban className="mr-2 h-4 w-4" />
                  {campaign.status === 'SENDING' ? 'Stop Send' : 'Cancel'}
                </DropdownMenuItem>
              )}
              {editable && (
                <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => onDelete(campaign)}>
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-2 gap-4 px-5 pt-4 pb-4 sm:grid-cols-3 lg:grid-cols-5">
          <StatMetric label="Delivered" value={summary.counts.delivered} rate={summary.rates.deliveryRate} />
          <StatMetric label="Opened" value={summary.counts.opened} rate={summary.rates.openRate} />
          <StatMetric label="Clicked" value={summary.counts.clicked} rate={summary.rates.clickRate} />
          <StatMetric label="Bounced" value={summary.counts.bounced} rate={summary.rates.bounceRate} />
          <StatMetric label="Unsubscribed" value={summary.counts.unsubscribed} rate={summary.rates.unsubscribeRate} />
        </div>
      )}
    </Card>
  )
}

export default CampaignCard
